import { differenceInCalendarMonths, min, parseISO } from 'date-fns'
import { Decimal } from 'decimal.js'

import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import { CalculadoraDeParcelasBase } from '@/features/simulador/shared/domain/calculadora-de-parcelas'
import { CalculadoraDeValorPresenteBase } from '@/features/simulador/shared/domain/calculadora-de-valor-presente'
import { ConversorJuros } from '@/features/simulador/shared/domain/conversor-juros'
import type { Fator } from '@/features/simulador/shared/domain/mascara'
import type { IParcela, TTipoParcela } from '@/features/simulador/shared/types/calculadora-parcela.types'

const PARAMETROS_DA_POLITICA = [
  'taxaDeDescontoExtra',
  'percentualDePoupancaMinimaParaDescontoExtra',
  'percentualDePoupancaMinimaNaMetadeDoPrazoDePagamento',
  'valorAVistaVendedor',
  'valorAVistaGcom',
  'valorAVistaGr',
] as const

const TIPOS_DE_PARCELA_DE_POUPANCA = new Set<TTipoParcela>(['entradas', 'mensais', 'intermediarias'])

type TParametrosDescontoExtra = Pick<TParametrosRotinaArquivo, (typeof PARAMETROS_DA_POLITICA)[number]>

type TPoliticaDeDescontoExtra = { [Parametro in keyof TParametrosDescontoExtra]-?: number }

export type TElegibilidadeDescontoExtra = {
  percentualDePoupanca: number
  mesesDoPrazo: number
  mesesDaMetadeDoPrazo: number
  percentualDePoupancaNaMetadeDoPrazo: number
  elegivel: boolean
}

export type TDescontoExtra = {
  taxaDiaria: number
  valorPresente: number
  fatores: Fator
}

const isPoliticaHabilitada = (parametros: TParametrosDescontoExtra): parametros is TPoliticaDeDescontoExtra =>
  PARAMETROS_DA_POLITICA.every((parametro) => (parametros[parametro] ?? 0) > 0)

export class CalculadoraDeDescontoExtra {
  private readonly calculadoraValorPresente = new CalculadoraDeValorPresenteBase()
  private readonly calculadoraParcelas = new CalculadoraDeParcelasBase()

  private constructor(private readonly politica: TPoliticaDeDescontoExtra) {}

  static habilitar(parametros: TParametrosDescontoExtra): CalculadoraDeDescontoExtra | null {
    return isPoliticaHabilitada(parametros) ? new CalculadoraDeDescontoExtra(parametros) : null
  }

  calcular(parcelas: IParcela[], dataSimulacao: string, dataSaldo?: string | null): TDescontoExtra | null {
    if (!dataSaldo || !this.verificarElegibilidade(parcelas, dataSaldo).elegivel) return null

    return this.calcularNovoValorPresenteEFatores(parcelas, dataSimulacao)
  }

  calcularSacocH(
    parcelas: IParcela[],
    dataSimulacao: string,
    dataPrevisaoDeEntregaDoBloco?: string | null,
  ): TDescontoExtra | null {
    if (!dataPrevisaoDeEntregaDoBloco) return null
    if (!this.verificarElegibilidadeSacocH(parcelas, dataPrevisaoDeEntregaDoBloco).elegivel) return null

    return this.calcularNovoValorPresenteEFatores(parcelas, dataSimulacao)
  }

  verificarElegibilidade(parcelas: IParcela[], dataSaldo: string): TElegibilidadeDescontoExtra {
    const parcelasDePoupanca = parcelas.filter((parcela) => TIPOS_DE_PARCELA_DE_POUPANCA.has(parcela.tipo))

    return this.apurarElegibilidade(parcelas, parcelasDePoupanca, dataSaldo)
  }

  verificarElegibilidadeSacocH(
    parcelas: IParcela[],
    dataPrevisaoDeEntregaDoBloco: string,
  ): TElegibilidadeDescontoExtra {
    const dataEntrega = parseISO(dataPrevisaoDeEntregaDoBloco)
    const parcelasDePoupanca = parcelas.filter(
      (parcela) => differenceInCalendarMonths(parseISO(parcela.data), dataEntrega) <= 0,
    )

    return this.apurarElegibilidade(parcelas, parcelasDePoupanca, dataPrevisaoDeEntregaDoBloco)
  }

  private calcularNovoValorPresenteEFatores(parcelas: IParcela[], dataSimulacao: string): TDescontoExtra {
    const taxaDiaria = ConversorJuros.converterTaxaDeJurosAnualParaDiaria(this.politica.taxaDeDescontoExtra)
    const parcelasComValorPresente = this.calculadoraValorPresente.calcularValorPresenteDasParcelas(
      parcelas,
      dataSimulacao,
      taxaDiaria,
    )
    const valorPresente = this.calculadoraParcelas.somar(parcelasComValorPresente, 'valorPresente')

    return {
      taxaDiaria,
      valorPresente,
      fatores: {
        fatorVendedor: valorPresente / this.politica.valorAVistaVendedor,
        fatorGcom: valorPresente / this.politica.valorAVistaGcom,
        fatorGR: valorPresente / this.politica.valorAVistaGr,
      },
    }
  }

  private apurarElegibilidade(
    parcelas: IParcela[],
    parcelasDePoupanca: IParcela[],
    dataFinalDoPrazo: string,
  ): TElegibilidadeDescontoExtra {
    const dataPrimeiraParcela = min(parcelas.map((parcela) => parseISO(parcela.data)))
    const mesDoPrazo = (data: string) => differenceInCalendarMonths(parseISO(data), dataPrimeiraParcela)
    const mesesDoPrazo = mesDoPrazo(dataFinalDoPrazo) + 1
    const mesesDaMetadeDoPrazo = Math.ceil(mesesDoPrazo / 2)

    const parcelasDePoupancaNaMetadeDoPrazo = parcelasDePoupanca.filter(
      (parcela) => mesDoPrazo(parcela.data) < mesesDaMetadeDoPrazo,
    )

    const total = this.somarValores(parcelas)
    const percentualDePoupanca = this.calcularPercentual(parcelasDePoupanca, total)
    const percentualDePoupancaNaMetadeDoPrazo = this.calcularPercentual(parcelasDePoupancaNaMetadeDoPrazo, total)

    return {
      percentualDePoupanca: percentualDePoupanca.toNumber(),
      mesesDoPrazo,
      mesesDaMetadeDoPrazo,
      percentualDePoupancaNaMetadeDoPrazo: percentualDePoupancaNaMetadeDoPrazo.toNumber(),
      elegivel:
        percentualDePoupanca.gte(this.politica.percentualDePoupancaMinimaParaDescontoExtra) &&
        percentualDePoupancaNaMetadeDoPrazo.gte(this.politica.percentualDePoupancaMinimaNaMetadeDoPrazoDePagamento),
    }
  }

  private calcularPercentual(parcelas: IParcela[], total: Decimal) {
    return total.isZero() ? new Decimal(0) : this.somarValores(parcelas).times(100).div(total)
  }

  private somarValores(parcelas: IParcela[]) {
    return parcelas.reduce((soma, parcela) => soma.plus(parcela.valor), new Decimal(0))
  }
}
